/**
 * MazeGenerator - Procedurally generates playable mazes using recursive division
 */

export enum TileType {
    FLOOR = 0,
    WALL = 1,
    SPAWN = 2,
    POWERUP_SPAWN = 3,
}

export class MazeGenerator {
    private width: number = 0;
    private height: number = 0;
    private grid: TileType[][] = [];
    private random: () => number = Math.random;

    /**
     * Generate a new maze
     * @param width - Number of tiles wide
     * @param height - Number of tiles tall
     * @param seed - Optional seed for reproducible mazes
     */
    generate(width: number, height: number, seed?: number): TileType[][] {
        this.width = width;
        this.height = height;

        // Initialize seeded random if provided
        if (seed !== undefined) {
            this.random = this.seededRandom(seed);
        } else {
            this.random = Math.random;
        }

        // Initialize grid with floors
        this.grid = [];
        for (let y = 0; y < height; y++) {
            this.grid[y] = [];
            for (let x = 0; x < width; x++) {
                this.grid[y][x] = TileType.FLOOR;
            }
        }

        // Add outer walls
        this.addOuterWalls();

        // Apply recursive division
        this.divide(1, 1, width - 2, height - 2);

        // Add spawn points (corners and center areas)
        this.addSpawnPoints();

        // Add power-up spawn locations
        this.addPowerUpSpawns();

        // Validate connectivity
        this.ensureConnectivity();

        return this.grid;
    }

    /**
     * Generate a simple debug level - open arena with close spawn points
     * Useful for debugging multiplayer visibility
     * @param width - Number of tiles wide
     * @param height - Number of tiles tall
     */
    generateDebugLevel(width: number, height: number): TileType[][] {
        this.width = width;
        this.height = height;

        // Initialize grid with floors
        this.grid = [];
        for (let y = 0; y < height; y++) {
            this.grid[y] = [];
            for (let x = 0; x < width; x++) {
                this.grid[y][x] = TileType.FLOOR;
            }
        }

        // Add outer walls only
        this.addOuterWalls();

        // Add 4 spawn points close together in center
        const centerX = Math.floor(width / 2);
        const centerY = Math.floor(height / 2);

        // Spawn points in a 2x2 grid pattern, 3 tiles apart
        const offset = 3;
        const debugSpawns = [
            { x: centerX - offset, y: centerY - offset },  // Top-left
            { x: centerX + offset, y: centerY - offset },  // Top-right
            { x: centerX - offset, y: centerY + offset },  // Bottom-left
            { x: centerX + offset, y: centerY + offset },  // Bottom-right
        ];

        for (const spawn of debugSpawns) {
            if (spawn.x > 0 && spawn.x < width - 1 && spawn.y > 0 && spawn.y < height - 1) {
                this.grid[spawn.y][spawn.x] = TileType.SPAWN;
            }
        }

        return this.grid;
    }

    private seededRandom(seed: number): () => number {
        // Simple LCG random number generator
        let state = seed;
        return () => {
            state = (state * 1664525 + 1013904223) % 4294967296;
            return state / 4294967296;
        };
    }

    private addOuterWalls(): void {
        // Top and bottom walls
        for (let x = 0; x < this.width; x++) {
            this.grid[0][x] = TileType.WALL;
            this.grid[this.height - 1][x] = TileType.WALL;
        }
        // Left and right walls
        for (let y = 0; y < this.height; y++) {
            this.grid[y][0] = TileType.WALL;
            this.grid[y][this.width - 1] = TileType.WALL;
        }
    }

    private divide(x: number, y: number, width: number, height: number): void {
        // Minimum room size
        const minSize = 4;

        if (width < minSize || height < minSize) {
            return;
        }

        // Decide whether to divide horizontally or vertically
        // Prefer to divide along the longer dimension
        const horizontal = height > width ? true :
            width > height ? false :
                this.random() > 0.5;

        if (horizontal) {
            // Draw horizontal wall
            const wallY = y + 2 + Math.floor(this.random() * (height - 4));
            const passageX = x + Math.floor(this.random() * width);

            for (let i = x; i < x + width; i++) {
                if (i !== passageX) {
                    this.grid[wallY][i] = TileType.WALL;
                }
            }

            // Recursively divide sub-chambers
            this.divide(x, y, width, wallY - y);
            this.divide(x, wallY + 1, width, y + height - wallY - 1);
        } else {
            // Draw vertical wall
            const wallX = x + 2 + Math.floor(this.random() * (width - 4));
            const passageY = y + Math.floor(this.random() * height);

            for (let i = y; i < y + height; i++) {
                if (i !== passageY) {
                    this.grid[i][wallX] = TileType.WALL;
                }
            }

            // Recursively divide sub-chambers
            this.divide(x, y, wallX - x, height);
            this.divide(wallX + 1, y, x + width - wallX - 1, height);
        }
    }

    private addSpawnPoints(): void {
        // Add spawn points in corners (with some offset from walls)
        const offset = 2;
        const spawnLocations = [
            { x: offset, y: offset }, // Top-left
            { x: this.width - 1 - offset, y: offset }, // Top-right
            { x: offset, y: this.height - 1 - offset }, // Bottom-left
            { x: this.width - 1 - offset, y: this.height - 1 - offset }, // Bottom-right
        ];

        for (const loc of spawnLocations) {
            // Clear area around spawn point
            this.clearArea(loc.x, loc.y, 2);
            this.grid[loc.y][loc.x] = TileType.SPAWN;
        }

        // Add center spawn for 5+ player games
        const centerX = Math.floor(this.width / 2);
        const centerY = Math.floor(this.height / 2);
        this.clearArea(centerX, centerY, 2);
        this.grid[centerY][centerX] = TileType.SPAWN;
    }

    private addPowerUpSpawns(): void {
        // Add 4-6 power-up spawn locations
        const numPowerUps = 4 + Math.floor(this.random() * 3);
        let placed = 0;
        let attempts = 0;
        const maxAttempts = 100;

        while (placed < numPowerUps && attempts < maxAttempts) {
            const x = 2 + Math.floor(this.random() * (this.width - 4));
            const y = 2 + Math.floor(this.random() * (this.height - 4));

            if (this.grid[y][x] === TileType.FLOOR) {
                // Check if not too close to spawn points or other power-ups
                if (this.isValidPowerUpLocation(x, y)) {
                    this.grid[y][x] = TileType.POWERUP_SPAWN;
                    placed++;
                }
            }
            attempts++;
        }
    }

    private isValidPowerUpLocation(x: number, y: number): boolean {
        const minDistance = 5;

        for (let dy = -minDistance; dy <= minDistance; dy++) {
            for (let dx = -minDistance; dx <= minDistance; dx++) {
                const checkX = x + dx;
                const checkY = y + dy;

                if (checkX >= 0 && checkX < this.width && checkY >= 0 && checkY < this.height) {
                    const tile = this.grid[checkY][checkX];
                    if (tile === TileType.SPAWN || tile === TileType.POWERUP_SPAWN) {
                        return false;
                    }
                }
            }
        }
        return true;
    }

    private clearArea(centerX: number, centerY: number, radius: number): void {
        for (let dy = -radius; dy <= radius; dy++) {
            for (let dx = -radius; dx <= radius; dx++) {
                const x = centerX + dx;
                const y = centerY + dy;

                if (x > 0 && x < this.width - 1 && y > 0 && y < this.height - 1) {
                    if (this.grid[y][x] === TileType.WALL) {
                        this.grid[y][x] = TileType.FLOOR;
                    }
                }
            }
        }
    }

    private ensureConnectivity(): void {
        // Simple flood fill to check if all floor/spawn tiles are connected
        // If not, punch holes in walls to connect isolated areas
        const visited: boolean[][] = [];
        for (let y = 0; y < this.height; y++) {
            visited[y] = new Array(this.width).fill(false);
        }

        // Find first floor tile
        let startX = -1;
        let startY = -1;
        outer: for (let y = 1; y < this.height - 1; y++) {
            for (let x = 1; x < this.width - 1; x++) {
                if (this.isWalkable(this.grid[y][x])) {
                    startX = x;
                    startY = y;
                    break outer;
                }
            }
        }

        if (startX === -1) return;

        // Flood fill from start
        this.floodFill(startX, startY, visited);

        // Check for unvisited walkable tiles and connect them
        for (let y = 1; y < this.height - 1; y++) {
            for (let x = 1; x < this.width - 1; x++) {
                if (this.isWalkable(this.grid[y][x]) && !visited[y][x]) {
                    // Find path to visited area
                    this.connectToVisited(x, y, visited);
                    // Re-flood fill from this point
                    this.floodFill(x, y, visited);
                }
            }
        }
    }

    private isWalkable(tile: TileType): boolean {
        return tile === TileType.FLOOR || tile === TileType.SPAWN || tile === TileType.POWERUP_SPAWN;
    }

    private floodFill(startX: number, startY: number, visited: boolean[][]): void {
        const queue: { x: number; y: number }[] = [{ x: startX, y: startY }];

        while (queue.length > 0) {
            const { x, y } = queue.shift()!;

            if (x < 0 || x >= this.width || y < 0 || y >= this.height) continue;
            if (visited[y][x]) continue;
            if (!this.isWalkable(this.grid[y][x])) continue;

            visited[y][x] = true;

            queue.push({ x: x + 1, y });
            queue.push({ x: x - 1, y });
            queue.push({ x, y: y + 1 });
            queue.push({ x, y: y - 1 });
        }
    }

    private connectToVisited(fromX: number, fromY: number, visited: boolean[][]): void {
        // Simple approach: carve a path in a direction until we hit visited area
        const directions = [
            { dx: 1, dy: 0 },
            { dx: -1, dy: 0 },
            { dx: 0, dy: 1 },
            { dx: 0, dy: -1 },
        ];

        // Try each direction
        for (const dir of directions) {
            let x = fromX;
            let y = fromY;

            while (x > 0 && x < this.width - 1 && y > 0 && y < this.height - 1) {
                x += dir.dx;
                y += dir.dy;

                if (visited[y][x]) {
                    // Found visited area - carve path back
                    let px = fromX;
                    let py = fromY;
                    while (px !== x || py !== y) {
                        if (this.grid[py][px] === TileType.WALL) {
                            this.grid[py][px] = TileType.FLOOR;
                        }
                        px += dir.dx;
                        py += dir.dy;
                    }
                    return;
                }
            }
        }
    }
}
